/**
 * SPIKE (claude/opencode-framework-eval): opencode inbound-channel bridge.
 *
 * This is the opencode replacement for Claude Code's proprietary inbound rail.
 * On Claude Code, a channel MCP server pushes a `notifications/claude/channel`
 * notification that the runtime injects as a user turn (+ renders string-only
 * `meta` as a `<channel ...>` tag). opencode has no such push primitive; its
 * equivalent is an EXTERNAL bridge that drives the headless server:
 *
 *   inbound message
 *     → sender gate           (reuses the existing pure classifiers)
 *     → provenance framing     (folds the <channel>-tag meta into prompt text,
 *                               since opencode's PromptInput carries no meta)
 *     → send message           (POST /session/{id}/message — one blocking call
 *                               that runs the turn and returns the reply)
 *     → outbound send          (caller posts the reply to the platform)
 *
 * The webhook → pending-inbound-file machinery and the sender/peer classifiers
 * on the Claude Code path are framework-agnostic and are REUSED as-is; only the
 * injection tail (this file) is opencode-specific. The gate + outbound are
 * injected so the bridge is testable and so the real classifiers
 * (slack-inbound-filter, *-peer-classifier — which live in packages/mcp) plug
 * in without a core→mcp dependency.
 */

import type { OpencodeClient, CreateSessionParams } from './opencode-client.js';

/** A normalized inbound message, transport-agnostic. */
export interface InboundMessage {
  /** Channel id, e.g. 'slack' | 'telegram' | 'msteams' | 'direct-chat'. */
  channelId: string;
  /**
   * Stable key identifying the conversation (thread/DM). Maps 1:1 to an
   * opencode session, so a thread keeps its context across turns.
   */
  conversationKey: string;
  /** Platform sender id (Slack U…, Telegram chat id, …). */
  senderId: string;
  /** The human-visible message text. */
  text: string;
  /**
   * Provenance / reply-obligation, the same string-only key/values that would
   * become `<channel ...>` tag attributes on Claude Code (source, thread ts,
   * requires_reply, lane, …). Folded into the prompt on opencode.
   */
  meta?: Record<string, string>;
}

/** Sender-gate decision — the shape the existing classifiers already produce. */
export interface GateDecision {
  admit: boolean;
  /** Machine reason on a drop (e.g. 'sender_policy', 'peer_not_allowed'). */
  reason?: string;
}

export type SenderGate = (msg: InboundMessage) => GateDecision | Promise<GateDecision>;

export interface BridgeOptions {
  client: OpencodeClient;
  /** Sender/peer gate. Default: admit everything (spike only). */
  gate?: SenderGate;
  /** Session-create params (agent/model/title) applied to every new session. */
  sessionDefaults?: CreateSessionParams;
  /**
   * When true (default), the bridge waits for the turn to finish and returns the
   * assistant reply. When false it starts the turn and returns immediately
   * (fire-and-forget; the reply arrives via the caller's own subscription).
   */
  awaitReply?: boolean;
}

export type InboundResult =
  | { status: 'declined'; reason: string }
  | { status: 'admitted'; sessionID: string }
  | { status: 'replied'; sessionID: string; reply: string | null };

/**
 * A failed `handleInbound`, carrying the one fact the caller needs to decide
 * whether retrying is safe: whether the turn reached the agent before failing.
 *
 * ENG-9096 narrowed when this can be false. A client-side failure DURING a turn
 * (timeout, abort, dropped connection) does not stop the turn: opencode runs it
 * to completion and persists the reply regardless, so re-running `handleInbound`
 * would ask the agent the same question twice. Only a failure that provably
 * happened BEFORE the message was sent — today, a failed `createSession` — is
 * reported as retryable.
 */
export class InboundError extends Error {
  /**
   * False ONLY when the turn provably never reached the agent (createSession
   * failed). A failure once the message is in flight is reported as `true`: the
   * turn probably ran, so retrying it would duplicate it.
   */
  readonly admitted: boolean;
  readonly sessionID: string | undefined;
  override readonly cause: unknown;

  constructor(message: string, opts: { admitted: boolean; sessionID?: string; cause?: unknown }) {
    super(message);
    this.name = 'InboundError';
    this.admitted = opts.admitted;
    this.sessionID = opts.sessionID;
    this.cause = opts.cause;
  }
}

/**
 * Fold the inbound provenance into a prompt preamble. This is the opencode
 * analogue of Claude Code's `<channel ...>` tag: the agent's instructions tell
 * it to read this header for who/where/whether-to-reply. Kept to one line so it
 * doesn't dominate the turn.
 */
export function frameInboundPrompt(msg: InboundMessage): string {
  // Trusted channel/sender identity is emitted FIRST and any colliding `meta`
  // key of the same name is dropped, so a hostile inbound payload can never
  // spoof provenance (the header is the only provenance signal opencode gets, so
  // this is a security invariant). Remaining meta keys follow in insertion order.
  const attrs: Array<[string, string]> = [
    ['channel', msg.channelId],
    ['sender', msg.senderId],
  ];
  for (const [k, v] of Object.entries(msg.meta ?? {})) {
    if (k === 'channel' || k === 'sender') continue;
    attrs.push([k, v]);
  }
  const header = attrs
    .map(([k, v]) => `${k}=${String(v).replace(/\s+/g, ' ').trim()}`)
    .join(' ');
  return `<channel ${header}>\n${msg.text}`;
}

/**
 * Bridges normalized inbound channel messages into an opencode session and
 * (optionally) returns the agent's reply for the caller to send outbound.
 */
export class OpencodeInboundBridge {
  private readonly client: OpencodeClient;
  private readonly gate: SenderGate;
  private readonly sessionDefaults?: CreateSessionParams;
  private readonly awaitReply: boolean;
  /** conversationKey → sessionID (one session per thread/DM). */
  private readonly sessions = new Map<string, string>();
  /** conversationKey → in-flight createSession, so concurrent inbound for the
   *  same conversation share one session instead of racing to create two. */
  private readonly inFlight = new Map<string, Promise<string>>();
  /**
   * conversationKey → tail of the turn chain for that conversation. Turns on one
   * conversation run STRICTLY ONE AT A TIME.
   *
   * This is load-bearing for correctness, not a politeness measure (ENG-9096).
   * opencode queues overlapping turns correctly - the transcript comes out in
   * order and nothing is lost - but every concurrent `POST /session/{id}/message`
   * returns the SAME assistant message: the newest one, not the caller's own.
   * Measured: two overlapping turns, and the POST that submitted the FIRST
   * message received the reply to the SECOND, while the first turn's reply was
   * returned to nobody. On a channel that means one person being handed the
   * answer to someone else's question in the same thread.
   *
   * The response gives no way to detect this after the fact: it carries
   * `info.parentID` (the user message it answers) but the caller is never told
   * its own user-message id, so there is nothing to compare against. Serialising
   * is therefore the fix, not a mitigation - the alternative is undetectable.
   *
   * `opencode run --attach` did not need this: each spawn streamed only its own
   * turn, so correlation came free. This is the one property the subprocess had
   * that the HTTP path does not.
   */
  private readonly turnChains = new Map<string, Promise<unknown>>();

  constructor(opts: BridgeOptions) {
    this.client = opts.client;
    this.gate = opts.gate ?? (() => ({ admit: true }));
    this.sessionDefaults = opts.sessionDefaults;
    this.awaitReply = opts.awaitReply ?? true;
  }

  /**
   * Run `fn` only once every previously-queued turn for `conversationKey` has
   * settled.
   *
   * `then(fn, fn)` — the SAME handler on both arms — is what keeps the chain
   * alive: a predecessor that rejected still lets the next turn run, so one
   * failed turn cannot strand every later message in that conversation. The
   * promise returned to the caller is that same one, so the caller still sees
   * the real failure.
   *
   * There was a second, redundant guard here (storing a tail wrapped to always
   * resolve). It was removed deliberately. With both present, sabotaging EITHER
   * one alone still passed every test, because the other silently covered it —
   * so neither was actually protected by anything. One mechanism, one test that
   * fails when it goes. Storing the rejecting promise raises no unhandled
   * rejection, because it is the same object the caller already handled; that
   * was measured, not assumed.
   */
  private serialize<T>(conversationKey: string, fn: () => Promise<T>): Promise<T> {
    const prior = this.turnChains.get(conversationKey) ?? Promise.resolve();
    const next = prior.then(fn, fn);
    this.turnChains.set(conversationKey, next);
    return next;
  }

  /** Resolve (creating on first use) the opencode session for a conversation. */
  async ensureSession(conversationKey: string): Promise<string> {
    const existing = this.sessions.get(conversationKey);
    if (existing) return existing;
    let pending = this.inFlight.get(conversationKey);
    if (!pending) {
      pending = this.client
        .createSession(this.sessionDefaults)
        .then(({ sessionID }) => {
          this.sessions.set(conversationKey, sessionID);
          return sessionID;
        })
        .finally(() => {
          this.inFlight.delete(conversationKey);
        });
      this.inFlight.set(conversationKey, pending);
    }
    return pending;
  }

  /** Drop a cached session (e.g. after a revoke or an explicit reset). */
  resetSession(conversationKey: string): void {
    this.sessions.delete(conversationKey);
  }

  /**
   * Full inbound path: gate → frame → inject → (optionally) reply.
   *
   * `gate` and `awaitReply` are per-CALL concerns, not per-bridge: one bridge
   * is reused across every inbound for an agent (it owns the durable
   * conversationKey → sessionID map, so a thread keeps its context), but a
   * fire-and-forget system nudge (`awaitReply: false`) and a request/reply
   * channel turn (`awaitReply: true`), or a gated Slack turn and an ungated
   * webapp turn, share that one bridge. So a caller may override the
   * constructor defaults here; an omitted field falls back to the default.
   */
  async handleInbound(
    msg: InboundMessage,
    overrides?: { gate?: SenderGate; awaitReply?: boolean },
  ): Promise<InboundResult> {
    const gate = overrides?.gate ?? this.gate;
    const awaitReply = overrides?.awaitReply ?? this.awaitReply;

    const decision = await gate(msg);
    if (!decision.admit) {
      return { status: 'declined', reason: decision.reason ?? 'gate_denied' };
    }

    // --- pre-admit: nothing is in the session yet, so a failure here is safe
    // to retry. Errors are tagged admitted:false.
    let sessionID: string;
    try {
      sessionID = await this.ensureSession(msg.conversationKey);
    } catch (err) {
      throw new InboundError('opencode createSession failed (nothing admitted)', {
        admitted: false,
        cause: err,
      });
    }

    const framed = frameInboundPrompt(msg);
    const model = this.sessionDefaults?.model;

    if (!awaitReply) {
      // Fire-and-forget nudge: the turn must run, but the caller is not waiting.
      // Still serialised, so it cannot overlap a real turn on this conversation.
      // We must not reject an unhandled promise - but a silently swallowed
      // failure would drop the message after we already told the caller
      // 'admitted', with no way to retry. So LOG it. (ENG-8032; CodeRabbit #3659.)
      void this.serialize(msg.conversationKey, () =>
        this.client.sendMessage({ sessionID, text: framed, model }),
      ).catch((err) => {
        // Sanitized error CLASS only, never the raw error - it can carry
        // arbitrary response/output data (CWE-532). The class plus the session
        // id is enough to know a background turn was lost.
        console.error(
          `[opencode-bridge] fire-and-forget turn failed for session ${sessionID}:`,
          err instanceof Error ? err.name : typeof err,
        );
      });
      return { status: 'admitted', sessionID };
    }

    try {
      const { reply } = await this.serialize(msg.conversationKey, () =>
        this.client.sendMessage({ sessionID, text: framed, model }),
      );
      return { status: 'replied', sessionID, reply };
    } catch (err) {
      // A failed sendMessage is AMBIGUOUS about admission, and the safe reading
      // flipped with ENG-9096. Measured: a client that gives up mid-turn (abort
      // or request timeout) does NOT cancel the turn - opencode ran it to
      // completion and persisted the reply anyway. So a timeout must NOT be
      // redelivered; doing so re-asks a question the agent already answered.
      //
      // Errors are therefore tagged admitted:true, which stops redelivery. That
      // is deliberately the pessimistic choice: it also catches the genuinely
      // pre-admit failures (connection refused, server not yet bound) where a
      // retry would have been safe and useful. It is the right trade only
      // because the lost reply is now RECOVERABLE - unlike the old path, the
      // turn is readable from the session via getStructuredMessages, so the
      // reply can be picked up rather than re-asked for.
      throw new InboundError(
        'opencode turn failed; the turn may have run to completion regardless, so it is not redelivered',
        { admitted: true, sessionID, cause: err },
      );
    }
  }
}